import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Search, Download, Users, UserCheck, UserX, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import AdminLayout from './AdminLayout';

const STATUS_COLORS = {
  active: 'bg-green-500/20 text-green-400',
  unsubscribed: 'bg-white/5 text-white/25',
};

const STATUS_LABELS = {
  active: 'Ativo',
  unsubscribed: 'Cancelado',
};

const AdminNewsletter = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [togglingId, setTogglingId] = useState(null);

  const fetchSubscribers = useCallback(async () => {
    setLoading(true);
    let query = supabase
      .from('newsletter_subscribers')
      .select('*')
      .order('created_at', { ascending: false });
    if (filterStatus) query = query.eq('status', filterStatus);
    const { data } = await query;
    if (data) setSubscribers(data);
    setLoading(false);
  }, [filterStatus]);

  useEffect(() => { fetchSubscribers(); }, [fetchSubscribers]);

  const toggleStatus = async (item) => {
    setTogglingId(item.id);
    const newStatus = item.status === 'active' ? 'unsubscribed' : 'active';
    await supabase.from('newsletter_subscribers').update({ status: newStatus }).eq('id', item.id);
    await fetchSubscribers();
    setTogglingId(null);
  };

  const filtered = subscribers.filter(
    (s) => !search || s.email?.toLowerCase().includes(search.toLowerCase())
  );

  const activeCount = subscribers.filter((s) => s.status === 'active').length;
  const unsubCount = subscribers.filter((s) => s.status !== 'active').length;

  const exportCSV = () => {
    const headers = 'Email,Status,Data de Inscrição\n';
    const rows = filtered
      .map(
        (s) =>
          `"${s.email}","${STATUS_LABELS[s.status] || s.status}","${new Date(s.created_at).toLocaleDateString('pt-BR')}"`
      )
      .join('\n');
    const blob = new Blob([headers + rows], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ibrc-newsletter-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="font-oswald text-2xl uppercase text-white mb-1">Newsletter</h1>
          <p className="font-manrope text-sm text-white/25">{subscribers.length} inscritos no total</p>
        </div>
        <motion.button
          onClick={exportCSV}
          className="flex items-center gap-2 h-9 px-4 bg-white/5 border border-white/10 text-white/50 hover:text-white hover:border-[#FFC107]/30 text-xs font-manrope uppercase tracking-wider transition-all"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Download size={14} /> Exportar CSV
        </motion.button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="bg-[#141414] border border-white/5 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Users size={16} className="text-white/20" />
            <span className="font-manrope text-[10px] uppercase tracking-wider text-white/25">Total</span>
          </div>
          <p className="font-oswald text-2xl text-white">{subscribers.length}</p>
        </div>
        <div className="bg-[#141414] border border-white/5 p-4">
          <div className="flex items-center gap-2 mb-2">
            <UserCheck size={16} className="text-green-400/60" />
            <span className="font-manrope text-[10px] uppercase tracking-wider text-white/25">Ativos</span>
          </div>
          <p className="font-oswald text-2xl text-green-400">{activeCount}</p>
        </div>
        <div className="bg-[#141414] border border-white/5 p-4">
          <div className="flex items-center gap-2 mb-2">
            <UserX size={16} className="text-white/20" />
            <span className="font-manrope text-[10px] uppercase tracking-wider text-white/25">Cancelados</span>
          </div>
          <p className="font-oswald text-2xl text-white/30">{unsubCount}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="flex-1 min-w-[200px] relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/20" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por email..."
            className="w-full bg-[#141414] border border-white/5 pl-10 pr-4 py-2.5 text-sm font-manrope text-white placeholder:text-white/20 outline-none focus:border-[#FFC107]/30 transition-colors"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="bg-[#141414] border border-white/5 px-4 py-2.5 text-sm font-manrope text-white/50 outline-none appearance-none cursor-pointer focus:border-[#FFC107]/30"
        >
          <option value="">Todos os status</option>
          <option value="active">Ativos</option>
          <option value="unsubscribed">Cancelados</option>
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={24} className="text-white/20 animate-spin" />
        </div>
      ) : (
        <div className="bg-[#141414] border border-white/5 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="text-left py-3 px-4 font-manrope text-[10px] uppercase tracking-wider text-white/25">Email</th>
                  <th className="text-left py-3 px-4 font-manrope text-[10px] uppercase tracking-wider text-white/25 hidden sm:table-cell">Status</th>
                  <th className="text-left py-3 px-4 font-manrope text-[10px] uppercase tracking-wider text-white/25 hidden md:table-cell">Data de Inscrição</th>
                  <th className="py-3 px-4 w-24 text-right font-manrope text-[10px] uppercase tracking-wider text-white/25">Ação</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {filtered.map((sub) => (
                    <motion.tr
                      key={sub.id}
                      className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      layout
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Mail size={13} className="text-white/15 flex-shrink-0" />
                          <span className="font-manrope text-sm text-white/60 truncate">{sub.email}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 hidden sm:table-cell">
                        <span className={`inline-block px-2 py-0.5 text-[10px] font-manrope uppercase tracking-wider ${STATUS_COLORS[sub.status] || 'bg-white/5 text-white/30'}`}>
                          {STATUS_LABELS[sub.status] || sub.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 hidden md:table-cell font-manrope text-xs text-white/20">
                        {sub.created_at ? new Date(sub.created_at).toLocaleDateString('pt-BR') : '—'}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <button
                          onClick={() => toggleStatus(sub)}
                          disabled={togglingId === sub.id}
                          className={`text-[10px] font-manrope uppercase tracking-wider px-3 py-1.5 border transition-all disabled:opacity-50 ${
                            sub.status === 'active'
                              ? 'border-white/10 text-white/30 hover:text-red-400 hover:border-red-400/30'
                              : 'border-white/10 text-white/30 hover:text-green-400 hover:border-green-400/30'
                          }`}
                        >
                          {togglingId === sub.id ? (
                            <Loader2 size={10} className="animate-spin inline" />
                          ) : sub.status === 'active' ? (
                            'Cancelar'
                          ) : (
                            'Reativar'
                          )}
                        </button>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={4} className="py-12 text-center font-manrope text-sm text-white/15">
                      {loading ? 'Carregando...' : 'Nenhum inscrito encontrado.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {filtered.length > 0 && (
            <div className="border-t border-white/5 px-4 py-3 flex items-center justify-between">
              <p className="font-manrope text-xs text-white/20">
                Mostrando {filtered.length} de {subscribers.length} inscritos
              </p>
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="font-manrope text-[10px] uppercase tracking-wider text-white/20 hover:text-white transition-colors"
                >
                  Limpar busca
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminNewsletter;
